<script setup>
	import {
		computed,
		watch,
		ref,
		onMounted,
		onUnmounted,
		nextTick,
	} from 'vue';
	import { useStore } from 'vuex';
	import Events from 'shared/settings/events';
	import { stripHtmlTags } from 'shared/utils/stripHtml.utils';
	import { DsButton, DsText, DsModal, DsButtonGroup, DsIcon } from '@coloplast/design-system';
	import { track_login_start, track_sign_up_start } from 'shared/services/tracking/loginTrackingService.ts';

	import "@coloplast/design-system/dist/components/modal/ds-modal.css";

	const store = useStore();

	const props = defineProps({
		vm: {
			type: Object
		},
		show: {
			type: Boolean,
			default: false
		},
		signinHeadingProp: {
			type: String,
			default: null,
		},
		signupHeadingProp: {
			type: String,
			default: null,
		},
		signinUrlProp: {
			type: String,
			default: null,
		},
		signupReturnUrl: {
			type: String,
			default: null,
		},
		signinButtonTextProp: {
			type: String,
			default: null,
		},
		signupButtonTextProp: {
			type: String,
			default: null,
		},
		signupBodyText: {
			type: String,
			default: null,
		},
		proceedWithoutRegisteringUrlProp: {
			type: String,
			default: null,
		},
		proceedWithoutRegisteringUrlTextProp: {
			type: String,
			default: null,
		},
		actionOrder: {
			type: String,
			default: () => 'LoginProceedRegister',
		},
		disabledRedirectAfterClosing: {
			type: Boolean,
			default: false
		}
	});

	const
		itemsOrder = ref(props?.actionOrder?.match(/[A-Z][a-z]*/g)),
		signupHeading = ref(props.signupHeadingProp || props.vm.BaseViewModel.AuthenticateModalViewModel.SignUpHeading),
		signinHeading = ref(props.signinHeadingProp || props.vm.BaseViewModel.AuthenticateModalViewModel.SignInHeading),
		signinText = ref(props.signinButtonTextProp || props.vm.BaseViewModel.AuthenticateModalViewModel.SignInButtonText),
		signupText = ref(props.signupButtonTextProp || props.vm.BaseViewModel.AuthenticateModalViewModel.SignUpButtonText),
		proceedWithoutRegisteringText = ref(props.proceedWithoutRegisteringUrlTextProp || props.vm.BaseViewModel.AuthenticateModalViewModel.ProceedWithoutRegisteringUrlText),
		selectedVariantCode = computed(() => store.getters.getSelectedVariantCode),
		quantity = computed(() => store.getters.getQuantity),
		isSample = computed(() => store.getters.getSelectedVariantIsSample),
		isCut = computed(() => store.getters.getSelectedVariantIsCut),
		loginModalState = computed(() => store.state.globalCheckout.isLoginModalShow),
		signinLink = computed(() => props.signinUrlProp || props.vm.BaseViewModel.AuthenticateModalViewModel.SignInUrl),
		signinLinkWithReturnParams = computed(() => {
			if (selectedVariantCode.value) {
				return replaceUrlForAddToBasketAction(signinLink.value);
			}
			return signinLink.value;
		}),
		isHcpModal = computed(() => props.vm?.BaseViewModel?.AuthenticateModalViewModel?.IsHcpModal),
		hcpLayoutButtonsView = ref([ //relevant for hcp modal only
			{
				key: 'Login',
				linkText: signinText?.value,
				order: 1
			},
			{
				key: 'Proceed',
				text: stripHtmlTags(props?.vm?.BaseViewModel?.AuthenticateModalViewModel?.ProceedWithoutRegisteringDescriptionText),
				linkText: proceedWithoutRegisteringText?.value,
				order: 2,
			},
			{
				key: 'Register',
				text: signupHeading?.value,
				linkText: signupText?.value,
				link: props.signupReturnUrl,
				order: 3
			}
		]),
		lastLink = ref(null),
		authenticateModal = computed(() => props.vm?.BaseViewModel.AuthenticateModalViewModel ),
		isOpenSingupModal = ref(false);

	function close() {
		if ( isOpenSingupModal.value ) return false

		store.commit('globalCheckout/setLoginModalShow', !loginModalState.value);

		if(!document.referrer || props.disabledRedirectAfterClosing) return

		const referrerUrl = new URL(document.referrer)
		if (location.origin == referrerUrl.origin) location.href = document.referrer;
	}

	function clickHandler(buttonAction) {
		// buttonAction { 0: login, 1: sign up, 2: proceed without registration }
		switch (buttonAction) {
			case 0:
				redirect()
				break;
			case 1:
				triggerSignupFlow()
				break;
			case 2:
				proceedWithoutRegistration()
				break;

			default:
				redirect()
				break;
		}
	}

	function redirect() {
		track_login_start();
		window.location.assign(signinLinkWithReturnParams.value);
	}

	function triggerSignupFlow() {
		track_sign_up_start();
		let tmpSignupReturnUrl = props.signupReturnUrl || props.vm.BaseViewModel.AuthenticateModalViewModel.SignUpReturnUrl;
		if (selectedVariantCode?.value) {
			tmpSignupReturnUrl = replaceUrlForAddToBasketAction(tmpSignupReturnUrl);
		}

		const event = new CustomEvent(Events.ACTIVATE_CONSUMER_SIGNUP, {
			detail: tmpSignupReturnUrl
		});
		document.dispatchEvent(event);

		isOpenSingupModal.value = true
	}


	function proceedWithoutRegistration() {
		window.location = authenticateModal.value.ProceedWithoutRegistrationUrl;
	}

	function replaceUrlForAddToBasketAction(url) {
		return url
			.replaceAll('_pvc_', selectedVariantCode.value)
			.replaceAll('_qty_', quantity.value)
			.replaceAll('_issample_', isSample.value)
			.replaceAll('_iscut_', isCut.value);
	}

	watch(() => props.vm, () => {
		store.commit('globalCheckout/setLoginModalShow', props.show);
	}, { deep: true });

	watch(() => hcpLayoutButtonsView.value, (newVal) => {
	    //sort by order
		newVal.sort((a, b) => a.order > b.order ? 1 : -1);
		//extract last link
		lastLink.value = newVal.find(x => x.order === 3);
	}, { deep: true });

	onMounted(async () => {
		store.commit('globalCheckout/setLoginModalShow', props.show);

		//if hcp modal re-arrange buttons order according to option checked in epi settings
		if (isHcpModal?.value && itemsOrder?.value?.length) {
			hcpLayoutButtonsView.value.map((button) => button.order = itemsOrder?.value?.findIndex(item => item === button.key) + 1);
			await nextTick();
			const proceedLink = document.getElementById('proceedLink');
			if (proceedLink) {
				hcpLayoutButtonsView.value.find(x => x.key === 'Proceed').link = proceedLink.href;
			}
		}

		document.addEventListener(Events.INACTIVATE_CONSUMER_SIGNUP, () => {
			isOpenSingupModal.value = false
		});
	});

	onUnmounted(async () =>  {
		document.removeEventListener(Events.INACTIVATE_CONSUMER_SIGNUP,() => {
			isOpenSingupModal.value = false
		});
	})
</script>

<template>
	<DsModal
		class="signup-modal"
		:isOpen="loginModalState"
		@close-modal="close"
		closeLabel="Close button"
		:title="isHcpModal ? signinHeading : authenticateModal.AuthenticateModalHeading"
		:size="isHcpModal ? 'md' : 'sm'"
		>
		<div v-if="!isHcpModal">
			<div class="signin-primary" v-if="authenticateModal.PrimaryButtonViewModel.ToShow">
				<ul class="signin-usp-list">
					<li v-for="item in authenticateModal.UspList" :key="item.Text" >
						<DsIcon :icon-name="item.IconName" size="sm"/>
						<DsText html-element="div"
						text-type="body-md"
						text-style="regular"
						>
							{{ item.Text }}
						</DsText>
					</li>
				</ul>

				<DsButtonGroup desktopAlignment="stretch">
					<DsButton
						:clickHandler="() => clickHandler(authenticateModal.PrimaryButtonViewModel.ButtonAction)"
							variant="primary"
							autofocus>
						{{ authenticateModal.PrimaryButtonViewModel.Label }}
					</DsButton>
				</DsButtonGroup>

			</div>

			<div class="signin-supporting" v-if="authenticateModal.SupportingTextAndLinkViewModel.ToShow">
				<DsText html-element="span"
						text-type="body-lg">
					<span v-html="authenticateModal.SupportingTextAndLinkViewModel.SupportingText" />
				</DsText>

				<DsButton
					:clickHandler="() => clickHandler(authenticateModal.SupportingTextAndLinkViewModel.ButtonAction)"
					variant="link"
					>
						{{ authenticateModal.SupportingTextAndLinkViewModel.Label }}
				</DsButton>
			</div>

			<div class="signin-secondary" v-if="authenticateModal.SecondaryButtonViewModel.ToShow">
				<DsText html-element="div"
						text-type="body-lg"
						class="signin-divider">
					<span v-html="authenticateModal.SecondaryButtonViewModel.DividerLabel" />
				</DsText>

				<DsButtonGroup desktopAlignment="stretch">
					<DsButton
						:clickHandler="() => clickHandler(authenticateModal.SecondaryButtonViewModel.ButtonAction)"
						variant="secondary"
						>
							{{ authenticateModal.SecondaryButtonViewModel.Label }}
					</DsButton>
				</DsButtonGroup>
			</div>
		</div>

		<!--Hcp Modal-->
		<div v-else>
			<DsText
				v-if="vm.BaseViewModel.AuthenticateModalViewModel.SignInDescriptionText"
				html-element="div"
				text-type="body-md"
				>
				<span v-html="vm.BaseViewModel.AuthenticateModalViewModel.SignInDescriptionText" />
			</DsText>
			<!--proceed href if exists-->
			<slot name="proceed"></slot>
			<div class="c-modal__hcp-buttons">
				<template v-for="button in hcpLayoutButtonsView">
					<DsButton v-if="button.order != 3 && button.linkText"
							:key="button.key"
							:variant="button.order === 1? 'primary' : 'secondary-neutral'"
							:href="button.link"
							@click="button.key === 'Login' ? redirect() : {}"
							@keydown.enter="button.key === 'Login' ? redirect() : {}"
							size="md"
							>
							<span v-html="button.linkText"></span>
					</DsButton>
				</template>
			</div>
			<div class="c-modal__hcp-text" v-if="lastLink && lastLink.linkText">
				<DsText
						v-if="lastLink.text"
						html-element="span"
						text-type="body-sm"
						>
						<span v-html="lastLink.text"></span>
				</DsText>
				<DsButton
						variant="link"
						size="sm"
						:href="lastLink.link"
						@click="lastLink.key === 'Login' ? redirect() : {}"
						@keydown.enter="lastLink.key === 'Login' ? redirect() : {}"
						>
					<span v-html="lastLink.linkText"></span>
				</DsButton>
			</div>
		</div>
	</DsModal>
</template>


<style lang="scss">
.signup-modal{
	&:focus {
		outline: 0;
	}

	.ds-modal__actions {
		margin-block-start: 0;
	}
}
</style>
<style lang="scss" scoped>
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.signin-usp-list {
	padding: 0;
	list-style: none;
	margin-block: ds-space("8") ds-space("9");

	li {
		display: flex;
		color: ds-color("fg","brand");
		margin-block-start: ds-space("5");

		div {
			color: ds-color("fg", "default");
			margin-left: ds-space("4");
		}
	}
}

.signin-supporting {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: ds-space("7");

	span {
		margin-right: ds-space("2");
	}
}

.signin-secondary {
	margin-top: ds-space("7");
}

.signin-divider {
	color: ds-color("fg", "default");
	text-align: center;
	margin-bottom: ds-space("7");
	position: relative;

	&::before,
	&::after {
		content: "";
		height: 1px;
		width: 45%;
		position: absolute;
		background: ds-color("border", "muted");
		top: 48%;
	}
	&::before {
		left: 0;
	}

	&::after {
		right: 0;
	}
}
</style>
